import React, { useCallback, useContext, useEffect, useState } from 'react';
import { observer, useLocalStore } from 'mobx-react';
import {
  Title,
  EmptyState,
  EmptyStateIcon,
  EmptyStateBody,
  Alert,
} from '@patternfly/react-core';
import { Helmet } from 'react-helmet';

import SearchIcon from '@patternfly/react-icons/dist/esm/icons/search-icon';

import { useTranslation } from 'react-i18next';
import { withErrorBoundary } from 'react-error-boundary';
import { CenteredLoadingSpinner, useFeature } from '@ole-ui/ole-ui-components';

import { storesContext } from 'stores';
import { handleError } from 'services/ErrorService';
import GenericViewError from 'components/Error/GenericViewError';
import ExamStore, { ExamStoreContext } from 'stores/Exam';
import './styles.scss';

const ExamComponent = React.lazy(() =>
  import(/* webpackChunkName: 'exam-component' */ 'components/Exam/Exam'),
);

const ExamView = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [versionedSlug, setVersionedSlug] = useState(null);

  const { alertStore, catalogStore, examsStore, routerStore } =
    useContext(storesContext);

  const { i18n, t } = useTranslation();
  const examStore = useLocalStore(() => new ExamStore(catalogStore));
  const { route } = routerStore;
  const { collateral } = examStore;

  const isExamReadinessEnabled = useFeature({
    section: 'exam',
    flagName: 'exam_readiness',
    defaultFlagValue: false,
  });

  const handleTabChange = useCallback(
    (_e, tabIndex) => {
      if (tabIndex === 1) {
        // readiness tab
        routerStore.navigate(
          'exams:view',
          {
            slug: route.params.slug,
            tab: 'readiness',
            ...(versionedSlug && { sku: versionedSlug }),
          },
          { replace: true },
        );
      } else {
        routerStore.navigate(
          'exams:view',
          {
            slug: route.params.slug,
            ...(versionedSlug && { sku: versionedSlug }),
          },
          { replace: true },
        );
      }

      setActiveTab(tabIndex);
    },
    [route, versionedSlug],
  );

  // isolating route params handling outside the exam component
  // to reduce internal deps for the component
  // tab handling:
  useEffect(() => {
    if (route && route.params) {
      const { tab } = route.params;

      // exhaustive checks necessary
      if (tab !== null && tab !== undefined && tab !== '') {
        // accouting for further tabs
        switch (tab) {
          case 'readiness':
            setActiveTab(1);
            break;
          default:
            setActiveTab(0);
            break;
        }
      } else {
        setActiveTab(0);
      }
    }
  }, [route]);

  // version handling:
  // sku has values like EX200V90K
  useEffect(() => {
    if (route && route.params) {
      const { sku } = route.params;

      if (sku !== null && sku !== undefined && sku !== '') {
        const [, version] = sku.toLowerCase().split('v');

        if (version) {
          examStore.version = `V${version.toUpperCase()}`;
          setVersionedSlug(sku);
        }
      }
    }
  }, [route]);

  useEffect(() => {
    if (route && route.params) {
      const { slug } = route.params;

      if (slug !== null && slug !== undefined && slug !== '') {
        examStore.code = slug;

        examStore.getUserExam();
        examStore.getExamCollateral(slug, i18n.language);

        examStore.startPolling();
      }
    }
    return () => {
      examStore.stopPolling();
    };
  }, [route, i18n.language]);

  useEffect(() => {
    examStore.getUserExam();
  }, [route, examStore.collateral]);

  useEffect(() => {
    if (isExamReadinessEnabled) {
      examStore.getExamFaqPlaylist();
    }
  }, [isExamReadinessEnabled]);

  const alerts = alertStore.currentAlerts.filter((alert) => {
    return alert.environment.includes(route.params.slug);
  });

  const title = collateral?.title || `${examStore.offering?.sku}`;
  const pageTitle = examStore.offering?.sku
    ? `${examStore.offering?.sku} - ${title}`
    : title;

  return (
    <div className="container-grid-responsive">
      <React.Suspense
        fallback={
          <div className="exam-view__loading">
            <CenteredLoadingSpinner />
          </div>
        }
      >
        {examStore.offering && (
          <React.Fragment>
            <Helmet title={`${pageTitle} - Red Hat Learning`} />
            <div>
              <h2>{title}</h2>
            </div>
            <div className="exam-alerts">
              {alerts.map((alert) => (
                <Alert
                  isInline
                  variant={alert.priority}
                  title={alert.title}
                  className="exam-view__alert"
                >
                  <p>{alert.description}</p>
                </Alert>
              ))}
            </div>
            <ExamStoreContext.Provider value={examStore}>
              <ExamComponent
                activeTab={activeTab}
                handleTabChange={handleTabChange}
              />
            </ExamStoreContext.Provider>
          </React.Fragment>
        )}
      </React.Suspense>
      {examStore.offering === null && examsStore.loading && (
        <div className="exam-view__loading">
          <CenteredLoadingSpinner />
        </div>
      )}
      {!examStore.offering && !examsStore.loading && (
        <EmptyState>
          <EmptyStateIcon icon={SearchIcon} />
          <Title size="lg" headingLevel="h4">
            {t('Exam not found')}
          </Title>
          <EmptyStateBody>
            {t(
              "Sorry, we can't find this exam right now. Please check again later.",
            )}
          </EmptyStateBody>
        </EmptyState>
      )}
    </div>
  );
};

export default withErrorBoundary(
  observer(ExamView),
  GenericViewError,
  handleError,
);
